<template>
  <SectionPageShell
    section="packages"
    :title="packageTypeLabel || (isFreePackage ? 'Paket aktivieren' : 'Paket buchen')"
    :subtitle="selectedPackage ? (isFreePackage ? 'Paket aktivieren' : 'Paket buchen') : null"
    show-back-link
  >
    <PackagePaymentMethods />

    <div class="package-booking py-3 py-lg-4">
    <p v-if="selectedPackage" class="text-muted mb-4">
      Prüfen Sie das ausgewählte Paket und die monatlichen Kosten vor der Bestellung.
    </p>

    <div v-if="packagesStore.loading" class="alert alert-light border">
      Paket wird geladen...
    </div>

    <div v-else-if="selectedPackage" class="row g-4">
      <div class="col-12 col-xl-8">
        <section class="package-booking-card">
          <div class="package-booking-card__eyebrow">Ausgewähltes Paket</div>
          <h2 class="package-booking-card__title">{{ packageTypeLabel }}</h2>
          <div class="package-booking-card__price">
            <PriceAmount :cents="selectedPackage.price_cents" monthly block prominent />
          </div>
          <div class="package-booking-card__meta">
            <span>{{ quantityLabel }}</span>
            <span v-if="durationLabel">{{ durationLabel }}</span>
          </div>

          <div class="package-booking-card__section">
            <h3 class="h6 mb-3">Leistungsumfang</h3>
            <PackageAdvantages
              :base-type="selectedPackage.base_type"
              :source-kind="selectedPackage.source_kind"
              title=""
            />
          </div>
          <div v-if="willDeactivateActiveFreeObject" class="alert alert-warning border mt-4 mb-0" role="alert">
            <strong class="d-block mb-1">Wichtig: Ihr Free-Objekt wird deaktiviert.</strong>
            {{ freeObjectDeactivationWarning }}
          </div>
          <div v-if="hasOtherActiveFreePackage" class="alert alert-warning border mt-4 mb-0">
            Achtung, wenn Sie bereits ein Paket der Kategorie "free" aktiviert haben, wird dieses deaktiviert. Es kann immer jeweils nur ein freies Paket aktiviert sein. Sollten Sie in einer anderen Kategorie bereits ein aktives Vermarktungsobjekt erstellt haben, wird dieses automatisch deaktiviert.
          </div>
        </section>
      </div>

      <div class="col-12 col-xl-4">
        <aside class="package-booking-summary">
          <div class="package-booking-summary__eyebrow">Zusammenfassung</div>
          <div class="package-booking-summary__line">
            <span>Paket</span>
            <strong>{{ packageTypeLabel }}</strong>
          </div>
          <div class="package-booking-summary__line">
            <span>Preis</span>
            <strong>
              <PriceAmount :cents="selectedPackage.price_cents" monthly inline />
            </strong>
          </div>
          <div class="package-booking-summary__line">
            <span>Kontingent</span>
            <strong>{{ quantityLabel }}</strong>
          </div>
          <PackageContractTerms v-if="!isFreePackage" />

          <div v-if="isUpgradeBooking" class="alert alert-info border mt-3 mb-0">
            <strong>Hinweis zur Hochstufung:</strong><br>
            Bereits genutzt:
            <PriceAmount :cents="currentPackageUsedCents" inline />
            ({{ usedMonthPercentLabel }} der laufenden Periode).<br>
            Offenes Guthaben aus aktuellem Paket:
            <PriceAmount :cents="currentPackageRemainingCreditCents" inline />.<br>
            Jetzt fällig:
            <PriceAmount :cents="upgradeDueNowCents" inline />.<br>
            Ab der nächsten Abrechnungsperiode:
            <PriceAmount :cents="nextPeriodPriceCents" monthly inline />.
          </div>
          <div v-if="!isFreePackage" class="package-booking-consent mt-4">
            <div class="form-check">
              <input
                id="package-booking-terms-consent"
                v-model="hasAcceptedTerms"
                class="form-check-input"
                type="checkbox"
              >
              <label class="form-check-label" for="package-booking-terms-consent">
                Es gelten die Allgemeinen Geschäftsbedingungen der MANET Marketing GmbH. Für die beauftragten Leistungen gelten zusätzlich folgende besondere Bedingungen: [Webdesign/Softwareentwicklung] [Marketing/Onlinewerbung] [Hosting/Domain/E-Mail] [Onlineportale/Inserate/Pakete]. Die jeweils geltenden Bedingungen wurden vor Vertragsschluss bereitgestellt und können unter
                <a
                  href="https://www.manet-marketing.de/downloads-fuer-kunden/"
                  target="_blank"
                  rel="noopener noreferrer"
                >https://www.manet-marketing.de/downloads-fuer-kunden/</a>
                abgerufen werden.
              </label>
            </div>
          </div>
          <button
            type="button"
            class="btn btn-success btn-lg w-100 mt-4"
            :disabled="isSubmitting || (!isFreePackage && !hasAcceptedTerms)"
            @click="submitBooking"
          >
            {{ isFreePackage ? 'kostenfrei aktivieren' : 'Kostenpflichtig bestellen' }}
          </button>
        </aside>
      </div>
    </div>

    <div v-else class="alert alert-warning border">
      Das ausgewählte Paket konnte nicht gefunden werden.
    </div>
    </div>
  </SectionPageShell>
</template>

<script>
import SectionPageShell from "@/components/base_type/SectionPageShell.vue";
import PriceAmount from "@/components/general/PriceAmount.vue";
import {usePackagesStore} from "@/stores/packages";
import {useUserStore} from "@/stores/user";
import PackageAdvantages from "@/components/packages/PackageAdvantages.vue";
import PackageContractTerms from "@/components/packages/PackageContractTerms.vue";
import PackagePaymentMethods from "@/components/packages/PackagePaymentMethods.vue";
import Swal from "sweetalert2";

const PACKAGE_LABELS = {
  unterkunft: 'Unterkunft-Paket',
  entdecken: 'Entdecken-Paket',
  termine: 'Veranstaltungs-Paket',
  angebote: 'Angebots-Paket',
}

export default {
  name: "PackagesBook",
  components: {
    PackageAdvantages,
    PackageContractTerms,
    PackagePaymentMethods,
    PriceAmount,
    SectionPageShell,
  },
  setup() {
    return {
      packagesStore: usePackagesStore(),
      userStore: useUserStore(),
    }
  },
  data() {
    return {
      isSubmitting: false,
      hasAcceptedTerms: false,
      currentPackageAssignment: null,
    }
  },
  async created() {
    if (!this.packagesStore.items.length) {
      await this.packagesStore.index()
    }

    if (this.isUpgradePath) {
      await this.fetchCurrentPackageAssignment()
    }
  },
  computed: {
    packageId() {
      return Number(this.$route.params.id)
    },
    upgradePackageId() {
      return Number(this.$route.query.upgrade_package_id)
    },
    isUpgradePath() {
      return Boolean(this.$route.query.upgrade_package_id)
    },
    selectedPackage() {
      if (this.isUpgradePath) {
        return this.packagesStore.items.find((pkg) => Number(pkg.id) === this.upgradePackageId) || null
      }
      return this.packagesStore.items.find((pkg) => Number(pkg.id) === this.packageId) || null
    },
    isFreePackage() {
      return this.selectedPackage?.source_kind === 'free'
    },
    activeFreePackage() {
      const activePackages = this.userStore.user?.organisation?.active_packages || []
      return activePackages.find((pkg) => pkg?.source_kind === 'free') || null
    },
    activePaidPackageForBaseType() {
      if (this.currentPackageAssignment?.package) {
        return {
          ...this.currentPackageAssignment.package,
          created_at: this.currentPackageAssignment.created_at,
          extra_quantity: this.currentPackageAssignment.extra_quantity,
        }
      }

      const activePackages = this.userStore.user?.organisation?.active_packages || []
      return activePackages.find((pkg) => (
        pkg?.source_kind === 'paid'
        && pkg?.base_type === this.selectedPackage?.base_type
      )) || null
    },
    hasOtherActiveFreePackage() {
      if (!this.isFreePackage || !this.activeFreePackage) {
        return false
      }

      const selectedIdentity = String(this.selectedPackage?.package_id ?? this.selectedPackage?.id ?? '')
      const activeIdentity = String(this.activeFreePackage?.package_id ?? this.activeFreePackage?.id ?? '')
      return selectedIdentity !== activeIdentity
    },
    willDeactivateActiveFreeObject() {
      return Boolean(
        !this.isFreePackage
        && this.activeFreePackage?.base_type
        && this.activeFreePackage.base_type !== this.selectedPackage?.base_type
      )
    },
    freeObjectDeactivationWarning() {
      const activeFreePackageLabel = PACKAGE_LABELS[this.activeFreePackage?.base_type] || 'Free-Paket'
      return `Wenn Sie jetzt das Paket „${this.packageTypeLabel}“ kaufen, wird Ihr aktives ${activeFreePackageLabel} beendet. Ein darüber veröffentlichtes Objekt wird automatisch deaktiviert und ist anschließend nicht mehr sichtbar.`
    },
    packageTypeLabel() {
      return PACKAGE_LABELS[this.selectedPackage?.base_type] || 'Vermarktungs-Paket'
    },
    quantityValue() {
      return Number(this.selectedPackage?.quantity ?? this.selectedPackage?.effective_quantity ?? 0)
    },
    quantityLabel() {
      if (!this.quantityValue) {
        return 'Flexible Anzahl an Einträgen'
      }

      return `${this.quantityValue} ${this.quantityValue === 1 ? 'Eintrag' : 'Einträge'}`
    },
    durationLabel() {
      return this.selectedPackage?.ends_at ? `Buchbar bis ${new Date(this.selectedPackage.ends_at).toLocaleDateString('de-DE')}` : ''
    },
    isUpgradeBooking() {
      if (!this.selectedPackage || this.selectedPackage?.source_kind !== 'paid' || !this.activePaidPackageForBaseType) {
        return false
      }

      const selectedIdentity = String(this.selectedPackage?.package_id ?? this.selectedPackage?.id ?? '')
      const activeIdentity = String(this.activePaidPackageForBaseType?.package_id ?? this.activePaidPackageForBaseType?.id ?? '')
      if (selectedIdentity === activeIdentity) {
        return false
      }

      const newPrice = Number(this.selectedPackage?.price_cents ?? 0)
      const currentPrice = Number(this.activePaidPackageForBaseType?.price_cents ?? 0)
      return newPrice > currentPrice
    },
    currentBillingUsedRatio() {
      const createdAt = this.activePaidPackageForBaseType?.created_at
      if (!createdAt) {
        return 0
      }

      const now = new Date()
      const assignmentCreatedAt = new Date(createdAt)
      if (Number.isNaN(assignmentCreatedAt.getTime())) {
        return 0
      }

      let periodStart = new Date(assignmentCreatedAt)
      let hasNextPeriod = true
      while (hasNextPeriod) {
        const nextPeriodStart = new Date(periodStart)
        nextPeriodStart.setMonth(nextPeriodStart.getMonth() + 1)
        if (nextPeriodStart <= now) {
          periodStart = nextPeriodStart
          continue
        }
        hasNextPeriod = false
      }

      const periodEnd = new Date(periodStart)
      periodEnd.setMonth(periodEnd.getMonth() + 1)
      const periodStartDate = new Date(periodStart.getFullYear(), periodStart.getMonth(), periodStart.getDate())
      const periodEndDate = new Date(periodEnd.getFullYear(), periodEnd.getMonth(), periodEnd.getDate())
      const nowDate = new Date(now.getFullYear(), now.getMonth(), now.getDate())

      const dayMs = 24 * 60 * 60 * 1000
      const totalDays = Math.floor((periodEndDate.getTime() - periodStartDate.getTime()) / dayMs)
      if (totalDays <= 0) {
        return 0
      }

      let elapsedDays = Math.max(Math.floor((nowDate.getTime() - periodStartDate.getTime()) / dayMs), 0)
      elapsedDays = Math.min(elapsedDays, totalDays)

      const remainingDays = totalDays - elapsedDays
      const remainingRatio = remainingDays / totalDays
      const usedRatio = 1 - remainingRatio
      return Math.min(Math.max(usedRatio, 0), 1)
    },
    usedMonthPercentLabel() {
      return `${Math.round(this.currentBillingUsedRatio * 100)}%`
    },
    currentPackagePriceCents() {
      return Number(this.activePaidPackageForBaseType?.price_cents ?? 0)
    },
    nextPeriodPriceCents() {
      return Number(this.selectedPackage?.price_cents ?? 0)
    },
    currentPackageUsedCents() {
      return Math.max(this.currentPackagePriceCents - this.currentPackageRemainingCreditCents, 0)
    },
    currentPackageRemainingCreditCents() {
      const remainingCredit = Math.floor(this.currentPackagePriceCents * (1 - this.currentBillingUsedRatio))
      return Math.max(remainingCredit, 0)
    },
    upgradeDueNowCents() {
      return Math.max(this.nextPeriodPriceCents - this.currentPackageRemainingCreditCents, 0)
    },
  },
  methods: {
    async fetchCurrentPackageAssignment() {
      const kdnr = this.userStore.user?.organisation?.kdnr || this.$route.query.kdnr
      const {data} = await this.packagesStore.apiClientV2.get(`/package_assignments/${this.packageId}`, {
        params: {
          ...(kdnr ? {kdnr} : {}),
        },
      })

      this.currentPackageAssignment = data?.package_assignment || null
    },
    async submitBooking() {
      if (!this.isFreePackage && !this.hasAcceptedTerms) {
        return
      }

      const kdnr = this.userStore.user?.organisation?.kdnr || this.$route.query.kdnr
      if (!kdnr || !this.selectedPackage?.id) {
        await Swal.fire('Fehler', 'Paket oder Kundennummer fehlt.', 'error')
        return
      }

      this.isSubmitting = true

      try {
        const paymentReturnUrl = new URL(
          this.$router.resolve({name: 'packages.payment_return'}).href,
          window.location.origin
        ).toString()

        const {data} = await this.packagesStore.apiClientV2.post('/payments', {
          kdnr,
          package_id: this.selectedPackage.id,
          redirect_url: paymentReturnUrl,
        })

        if (data?.checkout_url) {
          window.location.href = data.checkout_url
          return
        }

        const recurringPaidWithoutCheckout = (
          data?.error === false
          && !data?.checkout_url
          && String(data?.payment_transaction?.mollie?.status || '').toLowerCase() === 'paid'
        )

        if (recurringPaidWithoutCheckout) {
          await this.$router.push({
            name: 'packages.payment_return',
            query: {
              ...this.$route.query,
              wait_for_sync: 'true',
              payment_transaction_id: String(data?.payment_transaction?.id || ''),
            },
          })
          return
        }

        // Free packages may be activated immediately without hosted checkout.
        if (data && data.error === false) {
          await this.packagesStore.index()
          await this.userStore.apiUserData()
          await this.$router.push({name: 'packages.index'})
          return
        }

        throw new Error(data?.message || 'Keine Checkout-URL vom Zahlungsanbieter erhalten.')
      } catch (error) {
        if (error?.response) {
          return
        }
        await Swal.fire(
          'Zahlung konnte nicht gestartet werden',
          error?.message || 'Bitte versuchen Sie es erneut.',
          'error'
        )
      } finally {
        this.isSubmitting = false
      }
    },
  },
}
</script>

<style scoped lang="scss">
.package-booking-card,
.package-booking-summary {
  padding: 1.5rem;
  border: 1px solid #d8e1ea;
  background: #ffffff;
}

.package-booking-card__eyebrow,
.package-booking-summary__eyebrow {
  margin-bottom: 0.4rem;
  color: #5d7183;
  font-size: 0.76rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}

.package-booking-card__title {
  margin-bottom: 0.5rem;
  color: #10273d;
  font-size: 1.5rem;
}

.package-booking-card__price {
  margin-bottom: 0.5rem;
}

.package-booking-card__meta {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem 1rem;
  color: #4e6477;
}

.package-booking-card__section {
  margin-top: 1.5rem;
}

.package-booking-summary__line {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.5rem 0;
  border-bottom: 1px solid #e7edf3;
}

.package-booking-summary__line:last-of-type {
  border-bottom: 0;
}

.package-booking-summary__line span {
  color: #5d7183;
}

.package-booking-summary__line strong {
  color: #10273d;
  text-align: right;
}

.package-booking-consent {
  padding: 1rem;
  border: 1px solid #d8e1ea;
  background: #f8fafc;
  color: #334b5f;
  font-size: 0.875rem;
  line-height: 1.45;
}

.package-booking-consent .form-check-input {
  margin-top: 0.22rem;
}
</style>
